import { useMemo } from "react";
import {
  Box,
  Button,
  ComboBox,
  TextInput,
  Typography
} from "@dealer-center/core-components";
import type { ProviderSettingsProps } from "@dealer-center/ddc-pages-nrt-provider-config-tool-interface";
import { CARD_SX, CODE_BLOCK_SX, PANEL_HEADING_SX } from "./panelStyles";

// Bare keys with no wrapping braces, as the design shows them.
const formatSchema = (schema: Record<string, unknown>) =>
  JSON.stringify(schema, null, 2)
    .split("\n")
    .slice(1, -1)
    .map(line => line.replace(/^ {2}/, ""))
    .join("\n");

// Shown on Field while no Provider is picked.
const PROVIDER_GUIDANCE_PLACEHOLDER = "Select a provider first";
// Shown on Source Key/Default while a Provider is picked but no Field yet.
const FIELD_GUIDANCE_PLACEHOLDER = "Select a field first";
// AC-specified copy for the Field dropdown's own placeholder.
const FIELD_PLACEHOLDER = "Select a field to configure...";

const UNSAVED_BADGE_SX = {
  backgroundColor: "warning.light",
  borderRadius: 1,
  color: "warning.dark",
  display: "inline-block",
  fontWeight: 600,
  padding: "2px 8px",
  width: "fit-content"
} as const;

export const ProviderSettings = ({
  defaultValue,
  disabled,
  fieldOptions,
  hasUnsavedChanges,
  selectedField,
  sourceKey,
  onDefaultValueChange,
  onFieldChange,
  onReset,
  onSave,
  onSourceKeyChange
}: ProviderSettingsProps) => {
  // Both must be memoized or typing gets clobbered on unrelated re-renders.
  const fieldValue = useMemo(
    () => (selectedField ? [selectedField] : []),
    [selectedField]
  );

  // Source Key/Default/Save/Reset are all meaningless without a field
  // selected — nothing to edit, and nothing to save — until one is picked.
  const isFieldSelected = selectedField !== null;
  const areFieldDependentControlsDisabled = disabled || !isFieldSelected;

  const comboBoxOptions = useMemo(
    () => fieldOptions.map(({ label, value }) => ({ label, value })),
    [fieldOptions]
  );

  // Mirrors live form state, so it previews what Save would persist.
  const schemaPreview = useMemo(
    () =>
      formatSchema({
        sourceKey,
        setByUser: selectedField?.setByUser ?? false,
        type: selectedField?.type ?? "string",
        required: selectedField?.required ?? false,
        default: defaultValue || null,
        description: selectedField?.description ?? null,
        example: selectedField?.example ?? null,
        lastEditedBy: null,
        lastEditedDate: null
      }),
    [defaultValue, selectedField, sourceKey]
  );

  return (
    <Box sx={CARD_SX}>
      <Typography sx={PANEL_HEADING_SX} variant="h4">
        Provider Settings
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <ComboBox
          disabled={disabled}
          label="Field"
          name="field"
          options={comboBoxOptions}
          placeholder={
            disabled ? PROVIDER_GUIDANCE_PLACEHOLDER : FIELD_PLACEHOLDER
          }
          value={fieldValue}
          // Same as the Provider select: the selection lives on
          // `event.target.value`, and the lookup restores description/example.
          onChange={event => {
            const selectedValue = event.target.value?.[0]?.value;
            onFieldChange(
              fieldOptions.find(option => option.value === selectedValue) ??
                null
            );
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <TextInput
          disabled={areFieldDependentControlsDisabled}
          id="sourceKey"
          label="Source Key"
          name="sourceKey"
          placeholder={
            disabled
              ? PROVIDER_GUIDANCE_PLACEHOLDER
              : !isFieldSelected
                ? FIELD_GUIDANCE_PLACEHOLDER
                : undefined
          }
          value={sourceKey}
          onChange={e => onSourceKeyChange(e.target.value)}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <TextInput
          disabled={areFieldDependentControlsDisabled}
          id="defaultValue"
          label="Default"
          name="defaultValue"
          placeholder={
            disabled
              ? PROVIDER_GUIDANCE_PLACEHOLDER
              : !isFieldSelected
                ? FIELD_GUIDANCE_PLACEHOLDER
                : undefined
          }
          value={defaultValue}
          onChange={e => onDefaultValueChange(e.target.value)}
        />
      </Box>

      {hasUnsavedChanges && (
        <Typography
          data-testid="unsaved-changes-badge"
          sx={UNSAVED_BADGE_SX}
          variant="body-sm"
        >
          Unsaved changes
        </Typography>
      )}

      <Box
        data-testid="schema-preview"
        sx={{
          ...CODE_BLOCK_SX,
          borderLeft: "4px solid",
          borderLeftColor: "primary.main",
          height: "auto",
          maxHeight: "350px",
          minHeight: "160px"
        }}
      >
        {isFieldSelected ? schemaPreview : ""}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "flex-end"
        }}
      >
        <Button
          analyticsConfiguration={{ eventName: "nrt_provider_config_save" }}
          buttonStyle="primary"
          disabled={areFieldDependentControlsDisabled}
          onClick={onSave}
        >
          Save Configuration
        </Button>
        <Button
          analyticsConfiguration={{ eventName: "nrt_provider_config_reset" }}
          buttonStyle="secondary"
          disabled={areFieldDependentControlsDisabled}
          onClick={onReset}
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
};
